import { useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { FileCheck2, AlertTriangle, Upload, CalendarClock, X } from 'lucide-react';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import type { EPCR, Attachment } from '@/types';
import { RuleTag } from '@/components/RuleTag';

/**
 * CmnTracker — EMSPCR Foundation v1.0 §10.4.
 *
 * Physician Certification Statement (CMN / PCS) capture for Medicare
 * non-emergency transports. Requires:
 *   - CMN signed within 60 days (or an annual standing CMN for
 *     repetitive transports like dialysis)
 *   - Attending physician name + NPI
 *   - Scanned / uploaded document attached to the ePCR
 *
 * Surfaces three color states:
 *   green   — CMN valid (signed within 60 days, or standing CMN marked)
 *   amber   — CMN expiring within 7 days
 *   red     — CMN expired / missing
 *
 * Gates non-emergency billing submission (BillingTab reads
 * epcr.cmn_valid_through + the attachment id to decide whether to
 * block Submit).
 */

interface Props {
  epcr: EPCR;
  onUpdate: (patch: Partial<EPCR>) => void;
  /** Only show when the report is non-emergency (cascade engine flag). */
  required: boolean;
}

export function CmnTracker({ epcr, onUpdate, required }: Props) {
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Compute expiry from signed date (+60d) unless standing CMN.
  const status = useMemo(() => computeStatus(epcr), [epcr]);

  async function handleUpload(file: File) {
    if (!user) return;
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const att: Attachment = {
        id: crypto.randomUUID(),
        epcr_id: epcr.id,
        incident_id: null,
        fire_report_id: null,
        agency_id: epcr.agency_id,
        category: 'cmn',
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        // Local-only data URL until Supabase Storage upload swaps it
        // for a real object path. Keep the data_url on a loose cast
        // so existing attachments viewers can render offline.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        storage_path: `local:${dataUrl.slice(0, 32)}`,
        storage_bucket: 'local',
        caption: `CMN · ${file.name}`,
        taken_at: null,
        uploaded_by: user.id,
        created_at: new Date().toISOString(),
      };
      // Persist the raw data-url under a separate local index so the
      // attachment can still be displayed before sync. Cast avoids
      // touching the schema before a proper migration.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (att as any).data_url = dataUrl;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await db.attachments.add(withSyncMeta(att) as any);
      await enqueueSync('attachments', att.id, 'insert', att as unknown as Record<string, unknown>);
      onUpdate({ cmn_attachment_id: att.id });
    } finally {
      setUploading(false);
    }
  }

  function setSignedDate(d: string) {
    const signed = d || null;
    const validThrough = signed
      ? new Date(new Date(signed).getTime() + 60 * 24 * 3600 * 1000).toISOString().slice(0, 10)
      : null;
    onUpdate({
      cmn_signed_date: signed,
      cmn_valid_through: validThrough,
    });
  }

  function toggleStanding(on: boolean) {
    onUpdate({
      cmn_is_annual_standing: on,
      cmn_valid_through: on
        ? (epcr.cmn_signed_date
            ? new Date(new Date(epcr.cmn_signed_date).getTime() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10)
            : null)
        : epcr.cmn_signed_date
          ? new Date(new Date(epcr.cmn_signed_date).getTime() + 60 * 24 * 3600 * 1000).toISOString().slice(0, 10)
          : null,
    });
  }

  if (!required) return null;

  return (
    <section className="border border-emerald-800/60 bg-emerald-950/10">
      <header className="flex items-center justify-between border-b border-emerald-800/40 bg-emerald-950/30 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <FileCheck2 size={14} className="text-emerald-300" />
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-emerald-200">
            Physician Certification Statement (CMN / PCS)
          </h3>
        </div>
        <RuleTag
          sources={[
            { standard: 'CMS', element: '42 CFR §410.40', rationale: 'Medicare non-emergency transports require a CMN dated within 60 days.' },
          ]}
        />
      </header>
      <div className="p-3 space-y-3">
        {/* Status pill */}
        <div
          className={cn(
            'flex items-center gap-2 border px-3 py-1.5 text-xs',
            status.tone === 'ok' && 'border-emerald-700 bg-emerald-950/40 text-emerald-200',
            status.tone === 'warn' && 'border-amber-700 bg-amber-950/40 text-amber-200',
            status.tone === 'bad' && 'border-red-700 bg-red-950/40 text-red-200',
          )}
        >
          {status.tone === 'bad' ? <AlertTriangle size={14} /> : <CalendarClock size={14} />}
          <span className="font-bold uppercase tracking-widest text-[10px]">
            {status.label}
          </span>
          <span className="opacity-90">{status.detail}</span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">
              CMN Signed Date
            </span>
            <input
              type="date"
              value={epcr.cmn_signed_date ?? ''}
              onChange={(e) => setSignedDate(e.target.value)}
              disabled={epcr.locked}
              className="input-field text-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">
              Valid Through
            </span>
            <input
              type="date"
              value={epcr.cmn_valid_through ?? ''}
              readOnly
              disabled
              className="input-field text-sm font-mono opacity-80"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">
              Attending Physician
            </span>
            <input
              type="text"
              value={epcr.cmn_physician_name ?? ''}
              onChange={(e) => onUpdate({ cmn_physician_name: e.target.value || null })}
              disabled={epcr.locked}
              className="input-field text-sm"
              placeholder="Last, First MD"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">
              Physician NPI
            </span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={10}
              value={epcr.cmn_physician_npi ?? ''}
              onChange={(e) => onUpdate({ cmn_physician_npi: e.target.value || null })}
              disabled={epcr.locked}
              className="input-field text-sm font-mono"
              placeholder="10-digit NPI"
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={!!epcr.cmn_is_annual_standing}
            onChange={(e) => toggleStanding(e.target.checked)}
            disabled={epcr.locked}
          />
          Annual standing CMN (dialysis / scheduled repetitive — 365-day validity)
        </label>

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }}
          />
          {epcr.cmn_attachment_id ? (
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xs text-emerald-300 font-mono">
                Attached · {epcr.cmn_attachment_id.slice(0, 8)}
              </span>
              <button
                onClick={() => onUpdate({ cmn_attachment_id: null })}
                disabled={epcr.locked}
                className="text-xs text-slate-500 hover:text-red-400 flex items-center gap-1"
                title="Remove attachment"
              >
                <X size={12} /> Remove
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={epcr.locked || uploading}
              className="btn-secondary text-sm gap-1"
            >
              <Upload size={14} /> {uploading ? 'Uploading…' : 'Upload CMN'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function computeStatus(epcr: EPCR): { tone: 'ok' | 'warn' | 'bad'; label: string; detail: string } {
  if (!epcr.cmn_attachment_id && !epcr.cmn_signed_date) {
    return { tone: 'bad', label: 'Missing', detail: 'Upload CMN or document signed date before billing submit.' };
  }
  if (!epcr.cmn_valid_through) {
    return { tone: 'warn', label: 'Date not set', detail: 'Enter CMN signed date to compute expiry.' };
  }
  const now = Date.now();
  const end = new Date(epcr.cmn_valid_through + 'T23:59:59').getTime();
  const daysLeft = Math.ceil((end - now) / (24 * 3600 * 1000));
  if (daysLeft < 0) return { tone: 'bad', label: 'Expired', detail: `Expired ${Math.abs(daysLeft)}d ago — new CMN required.` };
  if (daysLeft <= 7) return { tone: 'warn', label: 'Expiring', detail: `${daysLeft}d remaining — re-verify before next run.` };
  return { tone: 'ok', label: 'Valid', detail: `${daysLeft}d remaining${epcr.cmn_is_annual_standing ? ' (annual standing)' : ''}.` };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}
